import {match} from 'ts-pattern';

import type {EagColumn} from '../../../../../types/EagColumn';
import type {EagColumnState} from '../../../../../types/EagColumnState';
import {isFilterValueActive} from '../../../../../utils/isFilterValueActive';
import type {LineHeight} from '../../../../../utils/lineHeightToValue';

type HeaderCellLayoutParams = {
  column: EagColumn;
  columnState: EagColumnState;
  width: number;
  headerHeight: LineHeight;
  showColumnMenu: boolean;
  columnSortMode: string;
  isMobile: boolean;
  filterState: Record<string, unknown>;
};

type HeaderCellLayout = {
  useSmallButtons: boolean;
  buttonSize: number;
  showMenuButton: boolean;
  showSortButton: boolean;
  textPaddingRight: number;
  isColumnFilterActive: boolean;
  menuPriority: 'primary' | undefined;
  columnHasMenuContent: boolean;
};

const BUTTON_GAP = 4;
const BUTTON_PAD_RIGHT = 4; // pr-1
const CELL_PADDING_LEFT = 8; // px-2
const MIN_TEXT_WIDTH = 8; // ~1 char at text-xs

/**
 * Computes button visibility, sizing, and text padding for a header cell.
 *
 * Progressive hiding: menu button hides first, then sort button,
 * based on available text width after subtracting button widths.
 */
export const computeHeaderCellLayout = (params: HeaderCellLayoutParams): HeaderCellLayout => {
  const useSmallButtons =
    params.headerHeight === 'COMPACT' ||
    params.headerHeight === 'SHORT' ||
    params.headerHeight === 'DEFAULT';
  const buttonSize = useSmallButtons ? 24 : 32;

  const twoButtonsWidth = buttonSize + BUTTON_GAP + buttonSize + BUTTON_PAD_RIGHT;
  const oneButtonWidth = buttonSize + BUTTON_PAD_RIGHT;

  const textSpaceWithBoth = params.width - twoButtonsWidth - CELL_PADDING_LEFT;
  const textSpaceWithOne = params.width - oneButtonWidth - CELL_PADDING_LEFT;

  // Hide menu button when the column has no column-specific actionable content.
  // showPinning / showAutosize are grid-level flags — available from any other column's menu.
  // Reset columns / Reset filters are global actions — not column-specific.
  // Exception: if the column is currently pinned, show the menu so the user can unpin it.
  const columnHasMenuContent =
    params.column.sortable === true ||
    params.column.filter != null ||
    params.columnState.pinned != null;

  const showMenuButton =
    params.showColumnMenu && columnHasMenuContent && textSpaceWithBoth >= MIN_TEXT_WIDTH;
  const showSortButton = params.columnSortMode !== 'NONE' && textSpaceWithOne >= MIN_TEXT_WIDTH;

  const isColumnFilterActive = isFilterValueActive(params.filterState[params.column.key]);
  const menuPriority = isColumnFilterActive ? ('primary' as const) : undefined;

  const textPaddingRight = match({
    isMobile: params.isMobile,
    showSortButton,
    showMenuButton,
  })
    .with({isMobile: true}, () => oneButtonWidth)
    .with({showSortButton: true, showMenuButton: true}, () => twoButtonsWidth)
    .with({showSortButton: true}, () => oneButtonWidth)
    .otherwise(() => 0);

  return {
    useSmallButtons,
    buttonSize,
    showMenuButton,
    showSortButton,
    textPaddingRight,
    isColumnFilterActive,
    menuPriority,
    columnHasMenuContent,
  };
};
